const money = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const formatQty = (count) =>
  Number.isInteger(count) ? `${count}` : Number(count).toFixed(2);

const measureUnit = (product) => {
  const inventory = product?.inventoryInfo || {};
  const metadata = product?.metadata || {};
  const unit = inventory.unit || product?.unit || metadata.unit || metadata.measureUnit;
  return String(unit || "ea").trim() || "ea";
};

/**
 * Receipt / order-info rollup.
 * The amount on the right is unit price × quantity.
 * The breakdown is hidden when quantity is 1 and modifiers add nothing.
 */
export function buildRollupLine(product) {
  const count = Number(product?.count ?? product?.quantity ?? 0) || 1;
  const hasSingle =
    product?.singlePrice !== undefined &&
    product?.singlePrice !== null &&
    product?.singlePrice !== "";

  if (!hasSingle) {
    const lineTotal = Number(product?.price ?? 0) || 0;
    return { count, lineTotal, description: null, useLineTotal: false };
  }

  const unitPrice = Number(product.singlePrice) || 0;
  const hasBase =
    product?.singlePriceWithoutModifier !== undefined &&
    product?.singlePriceWithoutModifier !== null &&
    product?.singlePriceWithoutModifier !== "";
  const basePrice = hasBase
    ? Number(product.singlePriceWithoutModifier) || 0
    : unitPrice;
  const optionsPrice = Math.max(unitPrice - basePrice, 0);
  const lineTotal = unitPrice * count;

  let description = null;
  if (!(count <= 1 && optionsPrice <= 0)) {
    const unit = measureUnit(product);
    const calculation = `(${money(basePrice)} + Mods ${money(optionsPrice)} = ${money(unitPrice)} ${unit})`;
    description =
      count <= 1 ? calculation : `${calculation} x${formatQty(count)}`;
  }

  return { count, lineTotal, description, useLineTotal: true };
}

const paidModifierCount = (count, freeCount) =>
  Math.max((Number(count) || 0) - (Number(freeCount) || 0), 0);

/**
 * Receipt modifier option: "+$1.00", hidden when the price is zero,
 * plus the free-count breakdown when some units are free.
 */
export function formatModifierOption(option, group) {
  if (!option || option.hideNameOnOrder === true) {
    return null;
  }

  const count = Number(option.count || 0);
  const freeCount = Number(option.freeCount || 0);
  const paidCount = paidModifierCount(count, freeCount);
  const hasUnit =
    option.singlePrice !== undefined &&
    option.singlePrice !== null &&
    option.singlePrice !== "";
  const unitPrice = hasUnit
    ? Number(option.singlePrice) || 0
    : paidCount > 0 && option.totalPrice != null
      ? (Number(option.totalPrice) || 0) / paidCount
      : Number(option.price || 0) || 0;
  const lineAmount = hasUnit
    ? unitPrice * paidCount
    : Number(option.totalPrice ?? option.price ?? unitPrice * paidCount) || 0;
  const isZero = Boolean(option.isFree) || lineAmount === 0;
  const priceLabel = isZero
    ? null
    : unitPrice > 0
      ? `+${money(unitPrice * (paidCount || count || 1))}`
      : money(lineAmount);
  const explanation =
    count > 0 && freeCount > 0
      ? `(${formatQty(count)} x ${money(unitPrice)} - ${freeCount} free = ${money(lineAmount)})`
      : null;
  const rawName = option?.name?.en || option?.name || "";
  const canSelectMultiple = group?.canSelectMultipleOptions !== false;
  const showQty =
    canSelectMultiple && count > 0 && (!option.isSizeOption || count > 1);

  return {
    name: freeCount > 0 ? `${rawName} (${freeCount} free)` : rawName,
    priceLabel,
    explanation,
    showQty,
    qtyLabel: `${formatQty(count)}×`,
  };
}
